/* eslint-disable react/prop-types */
import { FC, useState, useEffect } from "react";
import { Search, ChevronUp, ChevronDown } from "lucide-react";
import Pagination from "src/components/Pagination/Pagination";
import { calcReadPercentage, sortUsers } from "../../utils/analyticsHelpers";

interface Props {
	analytics: AnnouncementAnalytics;
}

type SortField = "name" | "department" | "read";
type SortDirection = "asc" | "desc";

const AnnouncementAnalytics: FC<Props> = ({ analytics }) => {
	const [search, setSearch] = useState("");
	const [sortField, setSortField] = useState<SortField>("name");
	const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
	const [statusFilter, setStatusFilter] = useState<"all" | "read" | "unread">(
		"all"
	);
	const [page, setPage] = useState(1);
	const pageSize = 20;

	const readPercentage = calcReadPercentage(
		analytics.readCount,
		analytics.totalRecipients
	);

	useEffect(() => {
		setPage(1);
	}, [search, statusFilter]);

	const toggleSort = (field: SortField) => {
		if (sortField === field) {
			setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
		} else {
			setSortField(field);
			setSortDirection("asc");
		}
	};

	const SortIcon: FC<{ field: SortField }> = ({ field }) => {
		if (sortField !== field)
			return <ChevronUp className="h-3.5 w-3.5 text-zinc-300" />;
		return sortDirection === "asc" ? (
			<ChevronUp className="h-3.5 w-3.5" />
		) : (
			<ChevronDown className="h-3.5 w-3.5" />
		);
	};

	const searchFiltered = analytics.users.filter((user) =>
		`${user.firstName} ${user.lastName}`
			.toLowerCase()
			.includes(search.toLowerCase())
	);

	const filtered =
		statusFilter === "all"
			? searchFiltered
			: statusFilter === "read"
				? searchFiltered.filter((u) => u.read)
				: searchFiltered.filter((u) => !u.read);

	const sorted = sortUsers(filtered, sortField, sortDirection);

	const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
	const safeCurrentPage = Math.min(page, totalPages);
	const paged = sorted.slice(
		(safeCurrentPage - 1) * pageSize,
		safeCurrentPage * pageSize
	);

	return (
		<div className="rounded-xl border border-border bg-subtle shadow-sm">
			<div className="p-6">
				<label className="font-heading text-lg font-medium text-foreground mb-4 block">
					Analytics
				</label>

				<div className="grid grid-cols-3 gap-4 mb-6">
					<div className="rounded-lg border border-border bg-white p-4">
						<p className="text-[13px] text-muted-foreground">
							Total Recipients
						</p>
						<p className="text-2xl font-semibold text-foreground mt-1">
							{analytics.totalRecipients}
						</p>
					</div>
					<div className="rounded-lg border border-border bg-white p-4">
						<p className="text-[13px] text-muted-foreground">Read</p>
						<p className="text-2xl font-semibold text-foreground mt-1">
							{analytics.readCount}
						</p>
					</div>
					<div className="rounded-lg border border-border bg-white p-4">
						<p className="text-[13px] text-muted-foreground">Read Rate</p>
						<div className="flex items-center gap-3 mt-1">
							<p className="text-2xl font-semibold text-foreground">
								{readPercentage}%
							</p>
							<div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
								<div
									className="h-full rounded-full bg-emerald-500 transition-all"
									style={{ width: `${readPercentage}%` }}
								/>
							</div>
						</div>
					</div>
				</div>

				<div className="relative mb-4 max-w-sm">
					<Search
						size={16}
						className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
					/>
					<input
						type="text"
						placeholder="Search users..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="rounded-lg border border-border bg-white pl-9 pr-4 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-ring"
					/>
				</div>

				<div
					className="flex items-center gap-2 mb-4"
					role="group"
					aria-label="Filter by read status"
				>
					{(["all", "read", "unread"] as const).map((filter) => (
						<button
							key={filter}
							type="button"
							onClick={() => setStatusFilter(filter)}
							aria-pressed={statusFilter === filter}
							className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
								statusFilter === filter
									? "bg-primary text-white"
									: "bg-muted text-muted-foreground hover:bg-muted/80"
							}`}
						>
							{filter === "all" ? "All" : filter === "read" ? "Read" : "Unread"}
						</button>
					))}
				</div>

				<div className="overflow-x-auto rounded-lg border border-border shadow-sm">
					<table className="w-full text-sm">
						<thead>
							<tr className="bg-white text-secondary-foreground text-sm font-medium border-b border-border">
								<th className="text-left px-4 py-3">
									<button
										type="button"
										onClick={() => toggleSort("name")}
										className="flex items-center gap-1"
									>
										Name <SortIcon field="name" />
									</button>
								</th>
								<th className="text-left px-4 py-3">
									<button
										type="button"
										onClick={() => toggleSort("department")}
										className="flex items-center gap-1"
									>
										Department <SortIcon field="department" />
									</button>
								</th>
								<th className="text-left px-4 py-3">
									<button
										type="button"
										onClick={() => toggleSort("read")}
										className="flex items-center gap-1"
									>
										Status <SortIcon field="read" />
									</button>
								</th>
								<th className="text-left px-4 py-3">Read at</th>
							</tr>
						</thead>
						<tbody>
							{paged.length === 0 && (
								<tr>
									<td
										colSpan={4}
										className="text-center py-8 text-muted-foreground"
									>
										No users found
									</td>
								</tr>
							)}
							{paged.map((user) => (
								<tr
									key={user.id}
									className="even:bg-zebra border-b border-border last:border-b-0"
								>
									<td className="px-4 py-3 font-medium">
										{user.firstName} {user.lastName}
									</td>
									<td className="px-4 py-3 text-muted-foreground">
										{user.department ?? "\u2014"}
									</td>
									<td className="px-4 py-3">
										{user.read ? (
											<span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700">
												Read
											</span>
										) : (
											<span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
												Unread
											</span>
										)}
									</td>
									<td className="px-4 py-3 text-muted-foreground">
										{user.readAt
											? new Date(user.readAt).toLocaleDateString("en-GB", {
													day: "numeric",
													month: "short",
													year: "numeric",
													hour: "2-digit",
													minute: "2-digit"
												})
											: "\u2014"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				<Pagination
					page={page}
					pageSize={pageSize}
					totalCount={sorted.length}
					onPageChange={setPage}
					itemLabel="users"
				/>
			</div>
		</div>
	);
};

export default AnnouncementAnalytics;
